// Stuck-slot reporting for the actor sync connection lock.
//
// The lock (`createActorSyncConnectionLock`) is a single-slot FIFO mutex. It used to
// have no timeout, and it was the ONE hang path in the app with no instrumentation at all:
//
//   - The DB watchdog (`slow-query-report`) times individual QUERIES. A read that
//     is queued behind a held slot has not started, so it is not a slow query —
//     it is not a query yet. Invisible.
//   - The async-step watchdog (`watch-async-step`) times navigator steps. It sees
//     "fetchQuery learnSummaryPage is stuck" but not WHY, and it only wraps the
//     navigator path, not a cold page mount.
//   - The Suspense boundary reports at 8s, but is gated off during first load —
//     precisely the cold-start path that skips `prepare()`.
//
// So a leaked slot produced an indefinite spinner with NO error, NO rejection and
// NO Sentry event. The lock now bounds every wait and poisons itself when a holder
// overruns its hold deadline (see its header), but that deadline is deliberately
// generous — it replaces the connection. This module reports much earlier, while the
// holder is still merely suspicious, and names the HOLDER that is blocking the queue,
// which is the one fact the deadline error and the other watchdogs cannot supply.
//
// Two edges, matching the DB watchdog's rationale exactly — a slot that is never
// released never reaches its own release handler, so the report has to be armed
// when the slot is TAKEN, not derived when it is freed.
//
// Failure-tolerant throughout: instrumentation must never be why a database
// operation fails, so every report path is wrapped and swallowed.

// NO STATIC SENTRY IMPORT — do NOT add one back.
//
// This module is reachable from `actor-sync-connection-lock.ts`, which the OPFS LEADER WORKER
// bundles (`ensure-turso-leader-worker-bundle.ts` runs `bun build` over
// `turso-leader-worker-entry.web.ts`). Importing `@-/dev.improve.haniverse.lib.sentry` pulls in
// `sentry-setup.web.tsx` → `@-/com.npmjs.varlock/env`, and varlock reads `process.env` from a
// Proxy `get` trap whose catch branch is UNGUARDED. A Web Worker has no `process`, so the worker
// threw `ReferenceError: process is not defined` AT STARTUP — killing the thread that owns the actor
// DB connection, so every actor query hung forever and the entire staging web maestro suite failed
// at launch (runs 33231390463 / 33237237844). Verified by driving the deployed site: the pageerror
// stack points at the worker's `blob:` URL, and the worker bundle went from 0 to 9 `process.*`
// references when that import was introduced.
//
// A dynamic `import()` does NOT help: the worker bundle is built without `--splitting`, so bun
// INLINES dynamic imports and the dependency lands in the bundle anyway (measured).
//
// So the reporter is INJECTED instead. The app installs the Sentry-backed one at startup
// (`installStuckLockReporter`); the worker never does, and simply reports nothing — which is
// correct, since the worker has no Sentry client to report through.

/** Sink for stuck-holder reports. Defaults to a no-op so a worker bundle needs no Sentry. */
export type StuckLockReporter = (report: {
  contexts: Record<string, Record<string, unknown>>;
  error: Error;
  fingerprint: string[];
  tags: Record<string, string>;
}) => void;

let reportToSink: StuckLockReporter = () => {};

/**
 * Install the process-wide sink for stuck-lock reports. Called once from app startup with a
 * Sentry-backed reporter; never called in the leader worker.
 */
export function installStuckLockReporter(reporter: StuckLockReporter): void {
  reportToSink = reporter;
}

/**
 * How long the slot may be held before the holder is anomalous.
 *
 * Deliberately well above the DB watchdog's 1s query threshold: a legitimate
 * holder is a single statement or one drizzle transaction, both far under this.
 * A holder past 5s is either a genuinely pathological write or — the case this
 * exists for — a slot that will never be released at all.
 */
const STUCK_HOLDER_THRESHOLD_MS = 5000;

/**
 * Escalations after the first report, so a permanently-leaked slot proves it never
 * came back without spamming forever. Same bounded-geometric shape as the DB and
 * async-step watchdogs.
 */
const STUCK_REESCALATION_MS = [15_000, 60_000] as const;

/** Sentry `fingerprint` root, so every stuck-slot report groups by holder label. */
const STUCK_FINGERPRINT = "actor-lock-slot-stuck";

/**
 * What took the slot. `begin` is the interesting one: it is the only holder that
 * spans multiple statements, and therefore the only one that can leak the slot by
 * never receiving its matching `commit`/`rollback` (see `turso-adapter`'s framing
 * and `track-orphaned-transaction`'s skipped-rollback case).
 */
export type LockHolderKind = "begin" | "statement" | "sync-apply" | "sync-push";

export interface StuckLockHolder {
  /** Threaded `[file:line:col]` when the caller supplied one. */
  callsite: string | null;
  kind: LockHolderKind;
  /** Parameterised SQL for a statement holder. Never bind values. */
  queryText: string | null;
  /** How many acquirers are queued behind this holder — the blast radius. */
  waiterCount: number;
}

/** SQL text is already parameterised; truncate for payload size + stable grouping. */
function redactQueryText(queryText: string): string {
  const collapsed = queryText.replaceAll(/\s+/gu, " ").trim();
  return collapsed.length > 200 ? `${collapsed.slice(0, 200)}…` : collapsed;
}

function reportStuckHolder(holder: StuckLockHolder, elapsedMs: number): void {
  const error = new Error(
    `Actor connection lock held by "${holder.kind}" for ${elapsedMs}ms; ${holder.waiterCount} operation(s) queued behind it`,
  );
  error.name = "StuckConnectionLockWatchdog";

  reportToSink({
    contexts: {
      "han.lock": {
        callsite: holder.callsite,
        elapsedMs,
        holder: holder.kind,
        queryText: holder.queryText === null ? null : redactQueryText(holder.queryText),
        state: "held",
        waiterCount: holder.waiterCount,
      },
    },
    error,
    // Group by holder kind, not by elapsed time or SQL: a single recurring leak
    // must be one issue, not one per duration bucket.
    fingerprint: [STUCK_FINGERPRINT, holder.kind],
    tags: {
      "han.lock.holder": holder.kind,
      "han.lock.stuck": "true",
    },
  });
}

/**
 * Arm a watchdog for a slot that was just acquired. Returns a disarm function the
 * caller MUST invoke when the slot is released.
 *
 * Pure observation: it never times the holder out and never rejects — the lock's
 * hold deadline does the policing, much later. The goal here is to NAME the holder
 * while it is still blocking, so the report points at the leak rather than at the
 * poisoning it eventually causes.
 */
export function watchLockHolder(getHolder: () => StuckLockHolder): () => void {
  const startTime = Date.now();
  const timers: ReturnType<typeof setTimeout>[] = [];

  const arm = (delayMs: number): void => {
    const timer = setTimeout(() => {
      try {
        reportStuckHolder(getHolder(), Date.now() - startTime);
      } catch {
        // Instrumentation must never surface as a database failure.
      }
    }, delayMs);
    // Jest (and any Node host) must not be kept alive by a diagnostic timer;
    // browsers/Hermes return a numeric id and skip this branch.
    const unref: unknown =
      typeof timer === "object" && timer !== null ? Reflect.get(timer, "unref") : undefined;
    if (typeof unref === "function") {
      Reflect.apply(unref, timer, []);
    }
    timers.push(timer);
  };

  arm(STUCK_HOLDER_THRESHOLD_MS);
  for (const extraDelayMs of STUCK_REESCALATION_MS) {
    arm(STUCK_HOLDER_THRESHOLD_MS + extraDelayMs);
  }

  return () => {
    for (const timer of timers) {
      clearTimeout(timer);
    }
  };
}
